import React, { useState, useMemo, useEffect } from "react";
import "./performance.less";
import {
  Reit,
  YearData,
  PerformanceBenchmark,
} from "../../../../types/reit.types";
import { safeFormat } from "../../../../utils/financialHelper";

interface PerformanceProps {
  reit: Reit | null;
}

interface PerformanceRow {
  metric: string;
  values: (string | number)[];
}

const Performance: React.FC<PerformanceProps> = ({ reit }) => {
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>("");

  const years = useMemo(() => {
    if (!reit?.years) return [];
    return reit.years.map((y: YearData) => `FY ${y.year}`);
  }, [reit]);

  useEffect(() => {
    if (years.length && !year) setYear(years[0]);
  }, [years, year]);

  const performanceData: PerformanceRow[] = useMemo(() => {
    if (!reit?.years?.length || !year) return [];

    const yearValue = year.replace("FY ", "");
    const yearData = reit.years.find((y) => y.year === yearValue);
    if (!yearData) return [];

    if (view === "Yearly") {
      const data: PerformanceBenchmark = yearData.performanceBenchmark;
      return Object.keys(data).map((key) => ({
        metric: data[key as keyof PerformanceBenchmark]?.key ?? key,
        values: [safeFormat(data[key as keyof PerformanceBenchmark]?.value)],
      }));
    } else {
      const quarters = (yearData as any).quarters || [];
      if (!quarters.length) return [];

      const keys = Object.keys(
        (quarters[0]?.performanceBenchmark as PerformanceBenchmark) || {}
      );

      return keys.map((key) => ({
        metric:
          quarters[0]?.performanceBenchmark?.[key as keyof PerformanceBenchmark]
            ?.key ?? key,
        values: quarters.map(
          (q: { performanceBenchmark: PerformanceBenchmark }) =>
            safeFormat(
              q.performanceBenchmark?.[key as keyof PerformanceBenchmark]?.value
            )
        ),
      }));
    }
  }, [reit, year, view]);

  return (
    <section className="performance">
      <div className="performance__card">
        {/* Header */}
        <div className="performance__header">
          <h2>Performance Benchmark</h2>
          <div className="performance__subheader">
            {/* Toggle */}
            <div className="performance__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>

            {/* Dropdown */}
            <div className="performance__dropdown">
              <div className="custom-select">
                <select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="performance__table">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                {view === "Quarterly" ? (
                  <>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                    <th>Q4</th>
                  </>
                ) : (
                  <th>Value</th>
                )}
              </tr>
            </thead>
            <tbody>
              {performanceData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.metric}</td>
                  {row.values.map((val, i) => (
                    <td key={i}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Performance;
